import graphApiSlice from 'src/shared/state/api-slices/graphApiSlice';
import {
    PortfolioMapFiltersQueryResult,
    portfolioMapFiltersQuery,
    SiteSpeciesRiskAssessmentQueryResult,
    siteSpeciesRiskAssessmentQuery,
    mapMarkersQuery,
    MapMarkersQueryResult,
    MapMarkersResponse,
    siteTableQuery,
    SiteTableQueryResult,
    SiteTableResponse,
    sitePopupQuery,
    SitePopupQueryResult,
    SitePopupData,
    sitePolygonsQuery,
    SitePolygonsQueryResult,
    SitePolygonData,
    nextMapMarkersQuery,
    NextMapMarkersQueryResult,
    nextSiteTableQuery,
    NextSiteTableQueryResult,
    nextSitePopupQuery,
    NextSitePopupQueryResult,
    siteSpeciesRiskQuery,
    SiteSpeciesRiskQueryResult,
    SiteSpeciesRiskData,
} from './porfolioSiteExplorerQuery';

type PortfolioMapFiltersQueryParams = {
    customerId: string;
};

type SiteSpeciesRiskAssessmentQueryParams = {
    siteId: string;
    filters?: { name: string; values: string[] }[];
};

type MapMarkersQueryParams = {
    customerId: string;
    metric?: string;
    bounds?: { north: number; south: number; east: number; west: number };
    filters?: { name: string; values: string[] }[];
    pagination?: { page: number; pageSize: number };
    sortBy?: { field: string; order: 'ASC' | 'DESC' };
};

type SiteTableQueryParams = {
    customerId: string;
    metric?: string;
    filters?: { name: string; values: string[] }[];
    pagination: { page: number; pageSize: number };
    sortBy?: { field: string; order: 'ASC' | 'DESC' };
    searchText?: string;
};

type SitePopupQueryParams = {
    customerId: string;
    siteId: string;
};

type SitePolygonsQueryParams = {
    customerId: string;
    siteId: string;
};

const PortfolioSiteExplorerGraphSlice = graphApiSlice.injectEndpoints({
    endpoints: builder => ({
        portfolioMapFilters: builder.query<
            PortfolioMapFiltersQueryResult['portfolio']['getPortfolioFilters'],
            PortfolioMapFiltersQueryParams
        >({
            query: params => ({
                body: portfolioMapFiltersQuery,
                variables: params,
            }),
            transformResponse: (response: PortfolioMapFiltersQueryResult) => response.portfolio.getPortfolioFilters,
        }),

        siteSpeciesRiskAssessment: builder.query<
            SiteSpeciesRiskAssessmentQueryResult['siteSummary']['getSpeciesRiskAssessment'],
            SiteSpeciesRiskAssessmentQueryParams
        >({
            query: params => ({
                body: siteSpeciesRiskAssessmentQuery,
                variables: params,
            }),
            transformResponse: response => response.siteSummary.getSpeciesRiskAssessment,
        }),

        // --- New purpose-built endpoints ---

        mapMarkers: builder.query<MapMarkersResponse, MapMarkersQueryParams>({
            query: params => ({
                body: mapMarkersQuery,
                variables: params,
            }),
            transformResponse: (response: MapMarkersQueryResult) => response.portfolio.getMapMarkers,
        }),
        siteTable: builder.query<SiteTableResponse, SiteTableQueryParams>({
            query: params => ({
                body: siteTableQuery,
                variables: params,
            }),
            transformResponse: (response: SiteTableQueryResult) => response.portfolio.getSiteTable,
        }),
        sitePopup: builder.query<SitePopupData, SitePopupQueryParams>({
            query: params => ({
                body: sitePopupQuery,
                variables: params,
            }),
            transformResponse: (response: SitePopupQueryResult) => response.portfolio.getSitePopup,
        }),
        // --- Grid-backed twins, used by the new page set via useSiteExplorerQueries ---

        nextMapMarkers: builder.query<MapMarkersResponse, MapMarkersQueryParams>({
            query: params => ({
                body: nextMapMarkersQuery,
                variables: params,
            }),
            transformResponse: (response: NextMapMarkersQueryResult) => response.portfolio.getNextMapMarkers,
        }),
        nextSiteTable: builder.query<SiteTableResponse, SiteTableQueryParams>({
            query: params => ({
                body: nextSiteTableQuery,
                variables: params,
            }),
            transformResponse: (response: NextSiteTableQueryResult) => response.portfolio.getNextSiteTable,
        }),
        nextSitePopup: builder.query<SitePopupData, SitePopupQueryParams>({
            query: params => ({
                body: nextSitePopupQuery,
                variables: params,
            }),
            transformResponse: (response: NextSitePopupQueryResult) => response.portfolio.getNextSitePopup,
        }),

        siteSpeciesRisk: builder.query<SiteSpeciesRiskData, SiteSpeciesRiskAssessmentQueryParams>({
            query: params => ({
                body: siteSpeciesRiskQuery,
                variables: params,
            }),
            transformResponse: (response: SiteSpeciesRiskQueryResult) => response.siteSummary.getSiteSpeciesRisk,
        }),

        sitePolygons: builder.query<SitePolygonData[], SitePolygonsQueryParams>({
            query: params => ({
                body: sitePolygonsQuery,
                variables: params,
            }),
            transformResponse: (response: SitePolygonsQueryResult) => response.portfolio.getSitePolygons,
        }),
    }),
});

export const {
    usePortfolioMapFiltersQuery,
    useSiteSpeciesRiskAssessmentQuery,
    useMapMarkersQuery,
    useSiteTableQuery,
    useSitePopupQuery,
    useLazySitePopupQuery,
    useSitePolygonsQuery,
    useLazySitePolygonsQuery,
    useNextMapMarkersQuery,
    useNextSiteTableQuery,
    useNextSitePopupQuery,
    useSiteSpeciesRiskQuery,
} = PortfolioSiteExplorerGraphSlice;

export default PortfolioSiteExplorerGraphSlice.reducer;
